import { Injectable } from '@angular/core';
import {
  SUPABASE_TABLES,
  SupabaseService,
} from '../../../shared/services/supabase.service';
import { UserService } from '../../../shared/services/user.service';
import { Router } from '@angular/router';
import { take } from 'rxjs';
import { ClubService } from 'src/app/shared/services/club.service';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { Database } from '@supabase/types';

@Injectable({
  providedIn: 'root',
})
export class ProfileService {
  userVerificationFeature: boolean = false;

  constructor(
    private supabaseService: SupabaseService,
    private userService: UserService,
    private clubsService: ClubService,
    private router: Router,
    private snackBarService: SnackBarService
  ) {}

  public showErrorSnackbar(message: string) {
    this.snackBarService.openSnackBar(message);
  }

  public updateUserProfile(
    url: string,
    input: Partial<UserProfileInfo>,
    onError?: () => void
  ) {
    const config = this.clubsService.flags();

    if (!config) {
      console.error('No timeslot flags loaded');
      onError?.();
      return;
    }
    this.userVerificationFeature = config.user_verification;
    this.userService
      .getUser()
      .pipe(take(1))
      .subscribe(async (user) => {
        if (user?.id) {
          const { email, fullname, phone, gender, birthday } = input;
          const birthdayStr =
            birthday instanceof Date
              ? birthday.toISOString()
              : (birthday ?? null);

          try {
            const res = await this.supabaseService.supabase
              .from(SUPABASE_TABLES.USERS)
              .update({
                email,
                fullname,
                phone,
                gender,
                birthday: birthdayStr,
              })
              .eq('user_id', user.id);

            if (res.error) {
              console.error('Profile update failed:', res.error);
              this.snackBarService.openSnackBar(
                'Something went wrong. Please try again later'
              );
              onError?.();
              return;
            }

            // The "an admin will contact you" page is only for a member who
            // really waits for approval. An approved member, or an admin who
            // fills in their details for the first time, is simply done.
            // ProfileInfoPendingGuard (parent of profile/updated-success)
            // notifies the admins about a waiting member exactly once.
            const waitsForApproval =
              url !== 'profileSettings' &&
              this.userVerificationFeature &&
              (await this.isConfirmedNotApproved(user.id));
            if (waitsForApproval) {
              this.router.navigate(['profile/updated-success']);
            } else {
              this.router.navigate(['profileSettings/updated-success']);
            }
          } catch (err: unknown) {
            console.error('Profile update error:', err);
            this.snackBarService.openSnackBar(
              'Something went wrong. Please try again later'
            );
            onError?.();
          }
        } else {
          onError?.();
        }
      });
  }

  /**
   * True only when the club CONFIRMS the member is not approved. A failed or
   * indeterminate lookup (no club id yet, RPC error) is not "not approved":
   * it must never send an approved member to the waiting page.
   */
  private async isConfirmedNotApproved(userId: string): Promise<boolean> {
    try {
      return (
        (await this.userService.getUserAndAssociatedClubs(userId)) === false
      );
    } catch {
      return false;
    }
  }

  public updateUserMeasurements(input: Partial<UserProfileInfo>) {
    this.userService
      .getUser()
      .pipe(take(1))
      .subscribe((user) => {
        if (user?.id) {
          this.supabaseService.supabase
            .from(SUPABASE_TABLES.USER_MESUREMENTS)
            .upsert(
              {
                user_id: user.id,
                tshirt_size: input.tshirt ?? null,
                shorts_size: input.shorts ?? null,
              },
              { onConflict: 'user_id' }
            )
            .then((res) => {
              this.router.navigate(['profileSettings/updated-success']);
            });
        }
      });
  }

  public updateUserStatement(statementId: string) {
    const userId = this.userService.currentUser?.id;
    if (!userId) return;
    return this.supabaseService.supabase
      .from(SUPABASE_TABLES.USERS)
      .update({
        // statement_id: statementId, // Field missing in types
        avatar_url: statementId, // Temporary fix: using avatar_url as placeholder if that's what it was intended for, or just comment it out
      })
      .eq('user_id', userId);
  }
  public async checkIfUserHasFullfiledPersonalInfo(userId: string) {
    const result = await this.supabaseService.supabase.rpc(
      'check_if_user_has_filed_personal_info',
      { p_user_id: userId }
    );
    return result.data;
  }

  public async updateAvatar(userId: string, avatarUrl: string) {
    return await this.supabaseService.supabase
      .from(SUPABASE_TABLES.USERS)
      .update({ avatar_url: avatarUrl })
      .eq('user_id', userId);
  }
}

export interface UserProfileInfo {
  email?: string;
  fullname?: string;
  club_id?: string;
  birthday?: Date | string | null;
  phone?: string;
  gender?: string;
  tshirt?: Database['public']['Enums']['clothing_size'] | null;
  shorts?: Database['public']['Enums']['clothing_size'] | null;
}
